import { Divider, Stack, Typography } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import type { FieldProps } from 'formik';
import { Field, FormikProvider, useFormikContext } from 'formik';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import {
  FormikCheckboxField,
  FormikCodeField,
  FormikTelField,
} from '@components/FormikFields';
import Popper from '@components/Popper/index';
import RoundedButton from '@components/RoundedButton/index';

import { isProduction } from '@constants/env';

import {
  useSendEmailVerificationCode,
  useSendSmsMfaCode,
  useSendTotpMfaCode,
} from '@hooks/api/session';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { MfaFormValues } from '@models/mfa';

import { MfaMethod } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'mfaSettings.title',
    defaultMessage: '{setting} {method} Authentication',
  },
  send: {
    id: 'mfaSettingsPhone.send',
    defaultMessage: 'Send code',
  },
  enterCode: {
    id: 'mfaSettings.enterCode',
    defaultMessage: 'After receiving a six-digit code, enter the code below',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  smsTitle: {
    id: 'mfaSettings.smsTitle',
    defaultMessage:
      'An authentication code will be sent to the phone number you provide',
  },
  totpTitle1: {
    id: 'mfaSettings.totpTitle1',
    defaultMessage: 'Scan this barcode or enter this ',
  },
  totpTitle2: {
    id: 'mfaSettings.totpTitle2',
    defaultMessage: ' into your authenticator app',
  },
  smsCodeError: {
    id: 'mfaSettings.smsCodeError',
    defaultMessage:
      'An error occurred while attempting to send a verification code to your phone.',
  },
  preferredHeader: {
    id: 'mfaSettings.preferredHeader',
    defaultMessage:
      'Do you want to set {method} MFA as your preferred method to use when logging in?',
  },
  preferredLabel: {
    id: 'mfaSettings.preferredLabel',
    defaultMessage:
      'Do you want to set {method} MFA as your preferred method to use when logging in?',
  },
  preferredCheckbox: {
    id: 'mfaSettings.preferredCheckbox',
    defaultMessage: 'Set {method} MFA as my preferred method',
  },
  enable: {
    id: 'GLOBAL.enable',
    defaultMessage: 'Enable',
  },
  disable: {
    id: 'GLOBAL.disable',
    defaultMessage: 'Disable',
  },
  [MfaMethod.SMS_MFA]: { id: 'mfaSettings.sms', defaultMessage: 'SMS' },
  [MfaMethod.SOFTWARE_TOKEN_MFA]: {
    id: 'mfaSettings.totp',
    defaultMessage: 'Authenticator App',
  },
  [MfaMethod.EMAIL_OTP]: {
    id: 'mfaSettings.emailOtp',
    defaultMessage: 'Email',
  },
  [MfaMethod.NONE]: {
    id: 'mfaSettings.noMfa',
    defaultMessage: 'None',
  },
  emailDisableTitle: {
    id: 'mfaSettings.emailDisableTitle',
    defaultMessage:
      'A verification code will be sent to your email to confirm disabling Email MFA',
  },
  emailEnableTitle: {
    id: 'mfaSettings.emailEnableTitle',
    defaultMessage:
      'Email MFA will be enabled. No verification code is required.',
  },
  sendEmailCode: {
    id: 'mfaSettings.sendEmailCode',
    defaultMessage: 'Send email code',
  },
  emailCodeSent: {
    id: 'mfaSettings.emailCodeSent',
    defaultMessage: 'Verification code sent to your email.',
  },
  emailCodeError: {
    id: 'mfaSettings.emailCodeError',
    defaultMessage:
      'An error occurred while sending a verification code to your email.',
  },
  code: {
    id: 'mfaSettings.code',
    defaultMessage: 'code',
  },
  recoveryViaSms: {
    id: 'mfaSettings.recoveryViaSms',
    defaultMessage:
      'Making {method} your preferred sign-in method will set SMS as your recovery option. A recovery code will be sent to {phoneNumber} if you ever lose access.',
  },
  recoveryViaEmail: {
    id: 'mfaSettings.recoveryViaEmail',
    defaultMessage:
      'Making {method} your preferred sign-in method will set email as your recovery option. A recovery code will be sent to your email if you ever lose access.',
  },
});

const VerifyAttribute: React.FC<
  RouteComponentProps & {
    enable: boolean;
    smsConfigured: boolean;
    totpConfigured: boolean;
    emailConfigured?: boolean;
    phoneNumber?: string | null;
  }
> = ({
  enable,
  smsConfigured,
  totpConfigured,
  emailConfigured,
  phoneNumber,
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [totpCode, setTotpCode] = useState<string | null>(null);
  const [popperOpen, setPopperOpen] = useState<boolean>(false);
  const handleClick = () => setPopperOpen(!popperOpen);
  const handleClose = () => setPopperOpen(false);

  const formik = useFormikContext<MfaFormValues>();
  const { isSubmitting, setFieldValue, submitForm, values } = formik;
  const {
    state: { activeProfile },
  } = useSession();
  const [email] = [activeProfile?.email];

  const [sendSmsCode] = useSendSmsMfaCode();
  const [sendTotpCode, { loading: sendTotpCodeLoading }] = useSendTotpMfaCode();
  const [sendEmailCode] = useSendEmailVerificationCode();

  const showTotp = values.method === MfaMethod.SOFTWARE_TOKEN_MFA;
  const showSms = values.method === MfaMethod.SMS_MFA;
  const showEmail = values.method === MfaMethod.EMAIL_OTP;

  const maskPhone = (phone: string) => {
    if (phone.length <= 4) return phone;
    const last4 = phone.slice(-4);
    return '+' + '*'.repeat(phone.length - 5) + last4;
  };

  // Count how many methods are already configured
  const configuredCount = [
    smsConfigured,
    totpConfigured,
    emailConfigured,
  ].filter(Boolean).length;
  // Display when enabling a new method and at least one is already configured
  const showPreferredMethod =
    enable &&
    configuredCount >= 1 &&
    ((values.method === MfaMethod.SOFTWARE_TOKEN_MFA && !totpConfigured) ||
      (values.method === MfaMethod.SMS_MFA && !smsConfigured) ||
      (values.method === MfaMethod.EMAIL_OTP && !emailConfigured));

  useEffect(() => {
    async function fetchTotpCode() {
      if (showTotp) {
        const response = await sendTotpCode();
        const code = response.data?.sendTotpMfaCode.code;
        if (code) {
          setTotpCode(code);
        }
      }
    }
    fetchTotpCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, showTotp]);

  const title = formatMessage(messages.title, {
    setting: formatMessage(enable ? messages.enable : messages.disable),
    method: showTotp
      ? formatMessage(messages[MfaMethod.SOFTWARE_TOKEN_MFA])
      : showSms
        ? formatMessage(messages[MfaMethod.SMS_MFA])
        : showEmail
          ? formatMessage(messages[MfaMethod.EMAIL_OTP])
          : '',
  });

  const issuer = `VITALL%20Engage${isProduction ? '' : '%20DEV'}`;
  const url = `otpauth://totp/${issuer}:${email}?secret=${totpCode}&issuer=${issuer}`;

  const handleSubmit = async () => {
    await submitForm();
  };

  return (
    <FormikProvider value={formik}>
      <Divider sx={{ mb: 2, mt: 1 }} />
      <ContentLoader loading={false}>
        <Stack>
          <Typography variant="h6">{title}</Typography>

          {showTotp && enable ? (
            <Stack>
              <Typography component="span" variant="caption">
                {formatMessage(messages.totpTitle1)}
                <Popper
                  buttonContent={
                    <Typography component="span" variant="caption">
                      {formatMessage(messages.code)}
                    </Typography>
                  }
                  copyContent
                  onClick={handleClick}
                  onClose={handleClose}
                  open={popperOpen}
                  popoverContent={totpCode ?? ''}
                  type="link"
                />
                {formatMessage(messages.totpTitle2)}
              </Typography>
              <ContentLoader loading={sendTotpCodeLoading}>
                <QRCodeSVG size={100} value={url} />
              </ContentLoader>
            </Stack>
          ) : null}

          {showSms ? (
            <>
              <Typography variant="caption">
                {formatMessage(messages.smsTitle)}
              </Typography>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Stack spacing={0}>
                  {enable && (
                    <FormikTelField
                      label={formatMessage(messages.phone)}
                      name="phone"
                      required
                    />
                  )}
                </Stack>

                <RoundedButton
                  autoSize
                  disabled={!/^\+[1-9]\d{1,14}$/.test(values.phone)}
                  fontSize={15}
                  height={20}
                  onClick={async () => {
                    const response = await sendSmsCode({
                      input: {
                        phoneNumber: values.phone,
                      },
                    });
                    const message = response.data?.sendSmsMfaCode.message;
                    enqueueSnackbar({
                      variant: message ? 'success' : 'error',
                      message: message ?? formatMessage(messages.smsCodeError),
                    });
                  }}
                >
                  {formatMessage(messages.send)}
                </RoundedButton>
              </Stack>
            </>
          ) : null}

          {showEmail ? (
            enable ? (
              <Typography variant="caption">
                {formatMessage(messages.emailEnableTitle)}
              </Typography>
            ) : (
              <>
                <Typography variant="caption">
                  {formatMessage(messages.emailDisableTitle)}
                </Typography>
                <RoundedButton
                  autoSize
                  fontSize={15}
                  height={20}
                  onClick={async () => {
                    const response = await sendEmailCode();
                    const message =
                      response.data?.sendEmailVerificationCode.message;
                    enqueueSnackbar({
                      variant: message ? 'success' : 'error',
                      message: message
                        ? formatMessage(messages.emailCodeSent)
                        : formatMessage(messages.emailCodeError),
                    });
                  }}
                >
                  {formatMessage(messages.sendEmailCode)}
                </RoundedButton>
              </>
            )
          ) : null}

          {showPreferredMethod ? (
            <>
              <Typography variant="body2" color="primary.main" mt={1}>
                {formatMessage(messages.preferredLabel, {
                  method: values.method
                    ? formatMessage(messages[values.method])
                    : '',
                })}
              </Typography>
              {values.method === MfaMethod.EMAIL_OTP && phoneNumber ? (
                <Typography variant="body2" color="primary.main" mt={1}>
                  {formatMessage(messages.recoveryViaSms, {
                    method: formatMessage(
                      messages[MfaMethod.EMAIL_OTP]
                    ).toLowerCase(),
                    phoneNumber: maskPhone(phoneNumber),
                  })}
                </Typography>
              ) : values.method === MfaMethod.SMS_MFA ? (
                <Typography variant="body2" color="primary.main" mt={1}>
                  {formatMessage(messages.recoveryViaEmail, {
                    method: formatMessage(
                      messages[MfaMethod.SMS_MFA]
                    ).toLowerCase(),
                  })}
                </Typography>
              ) : null}
              <FormikCheckboxField
                label={formatMessage(messages.preferredCheckbox, {
                  method: values.method
                    ? formatMessage(messages[values.method])
                    : '',
                })}
                name="preferredMethod"
                onChange={(value: boolean) => {
                  setFieldValue('preferredMethod', value);
                }}
              />
            </>
          ) : (
            enable &&
            (values.method === MfaMethod.EMAIL_OTP && phoneNumber ? (
              <Typography variant="body2" color="primary.main" mt={1}>
                {formatMessage(messages.recoveryViaSms, {
                  method: formatMessage(
                    messages[MfaMethod.EMAIL_OTP]
                  ).toLowerCase(),
                  phoneNumber: maskPhone(phoneNumber),
                })}
              </Typography>
            ) : values.method === MfaMethod.SMS_MFA ? (
              <Typography variant="body2" color="primary.main" mt={1}>
                {formatMessage(messages.recoveryViaEmail, {
                  method: formatMessage(
                    messages[MfaMethod.SMS_MFA]
                  ).toLowerCase(),
                })}
              </Typography>
            ) : null)
          )}

          {/* Email OTP enable needs no code; just show the submit button */}
          {showEmail && enable ? (
            <RoundedButton
              disabled={isSubmitting}
              height={35}
              onClick={handleSubmit}
              type="submit"
            >
              {title}
            </RoundedButton>
          ) : (
            <>
              <Typography mt={2} variant="body2">
                {formatMessage(messages.enterCode)}
              </Typography>
              <Field name="code">
                {({ field }: FieldProps<string>) => (
                  <FormikCodeField
                    {...field}
                    autoSubmit={false}
                    codeLength={values.code.length}
                  />
                )}
              </Field>

              <RoundedButton
                disabled={isSubmitting}
                height={35}
                onClick={handleSubmit}
                type="submit"
              >
                {title}
              </RoundedButton>
            </>
          )}
        </Stack>
      </ContentLoader>
    </FormikProvider>
  );
};

export default VerifyAttribute;
